import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from './db';
import { onSyncState, startSyncLoop, type SyncState } from './sync/engine';
import { useSession } from './session';
import { UpdatePrompt } from './UpdatePrompt';
import { ErrorBoundary } from './ErrorBoundary';
import { LoginScreen } from './routes/LoginScreen';
import { DashboardScreen } from './routes/DashboardScreen';
import { ListScreen } from './routes/ListScreen';
import { AddScreen } from './routes/AddScreen';
import { CompareScreen } from './routes/CompareScreen';
import { AisleOrderScreen } from './routes/AisleOrderScreen';
import { StatusScreen } from './routes/StatusScreen';
import { SettingsScreen } from './routes/SettingsScreen';
import { ActivityScreen } from './routes/ActivityScreen';
import { WeekScreen } from './agenda/WeekScreen';
import { PeopleScreen } from './agenda/PeopleScreen';
import { ChangesScreen } from './agenda/ChangesScreen';
import { startAgendaSync } from './agenda/sync';
import { TodosScreen } from './todos/TodosScreen';
import { startTodoSync } from './todos/sync';
import { ReceptenScreen } from './recepten/ReceptenScreen';
import { RecipeImportScreen } from './recepten/RecipeImportScreen';
import { RecipeSuggestScreen } from './recepten/RecipeSuggestScreen';
import { RecipeSettingsScreen } from './recepten/RecipeSettingsScreen';
import { RecipeScreen } from './recepten/RecipeScreen';
import { CookModeScreen } from './recepten/CookModeScreen';
import { startRecipeSync } from './recepten/sync';
import { PakketScreen } from './pakket/PakketScreen';
import { WordleScreen } from './wordle/WordleScreen';
import { HuubScreen } from './huub/HuubScreen';
import { startWordleSync } from './wordle/sync';
import { MorseScreen } from './morse/MorseScreen';
import { startMorseSync } from './morse/sync';
import { SchaakScreen } from './schaak/SchaakScreen';
import { startSchaakSync } from './schaak/sync';
import { canSeeModule } from './modules';
import { Loading } from './components/Loading';

/**
 * The shell.
 *
 * Two things live here rather than in a screen, because they are true
 * everywhere: the sync loop, and the honesty banners. Principle 6 — never hide
 * information, including staleness and uncertainty — means a queued edit and a
 * refused one are both visible without going looking for them.
 */

function StatusBanners({ state }: { state: SyncState }) {
  const queued = useLiveQuery(() => db.outbox.count(), [], 0);
  const rejections = useLiveQuery(() => db.rejections.toArray(), [], []);

  return (
    <>
      {state === 'offline' && queued > 0 && (
        <div className="banner">
          Offline — {queued} {queued === 1 ? 'wijziging wacht' : 'wijzigingen wachten'} op synchronisatie
        </div>
      )}
      {/* Offline with nothing queued is not worth a banner: the app works. */}
      {rejections.length > 0 && (
        <div className="banner error">
          {rejections.length === 1
            ? 'Eén wijziging kon niet worden opgeslagen'
            : `${rejections.length} wijzigingen konden niet worden opgeslagen`}
          {/* Dismissing was the only option before, which meant the person knew
              a change was lost but never which one. */}
          <Link className="btn btn-quiet" style={{ marginLeft: 'auto', minHeight: 32 }} to="/boodschappen/status">
            Bekijken
          </Link>
        </div>
      )}
    </>
  );
}

export function App() {
  const session = useSession();
  const [syncState, setSyncState] = useState<SyncState>('idle');

  useEffect(() => onSyncState(setSyncState), []);

  const displayName = session.status === 'in' ? session.me?.user.display_name : undefined;

  useEffect(() => {
    if (session.status !== 'in') return;
    // Each module runs its own independent sync loop.
    const stopGroceries = startSyncLoop();
    const stopAgenda = startAgendaSync();
    const stopTodos = startTodoSync();
    const stopRecipes = startRecipeSync();
    const stopWordle = startWordleSync();
    // A personal module syncs only on its owner's phone.
    const stopMorse = canSeeModule('morse', displayName) ? startMorseSync() : () => {};
    const stopSchaak = canSeeModule('schaak', displayName) ? startSchaakSync() : () => {};
    return () => {
      stopGroceries();
      stopAgenda();
      stopTodos();
      stopRecipes();
      stopWordle();
      stopMorse();
      stopSchaak();
    };
  }, [session.status, displayName]);

  if (session.status === 'loading') {
    return (
      <div className="app">
        <Loading />
      </div>
    );
  }

  // A session that expires or is revoked mid-use must not leave the app quietly
  // not syncing. The list still works — it is local — but the person has to
  // know why nothing is reaching the other phone.
  if (session.status === 'in' && syncState === 'signed_out') {
    return (
      <div className="app">
        <div className="banner">Je sessie is verlopen. Log opnieuw in om te blijven synchroniseren.</div>
        <LoginScreen />
      </div>
    );
  }

  if (session.status === 'out') {
    return (
      <div className="app">
        <UpdatePrompt />
        <LoginScreen />
      </div>
    );
  }

  return (
    <div className="app">
      <UpdatePrompt />
      <StatusBanners state={syncState} />
      {/* A render throw must not cost the household the whole app when the
          data behind it is intact. */}
      <ErrorBoundary>
        <Routes>
          <Route path="/" element={<DashboardScreen />} />
          {/* Hub-wide settings — notifications and the rest apply to every module. */}
          <Route path="/instellingen" element={<SettingsScreen />} />

          {/* Groceries module */}
          <Route path="/boodschappen" element={<ListScreen syncState={syncState} />} />
          <Route path="/boodschappen/add" element={<AddScreen />} />
          <Route path="/boodschappen/compare/:genericId" element={<CompareScreen />} />
          <Route path="/boodschappen/aisles/:storeCode" element={<AisleOrderScreen />} />
          <Route path="/boodschappen/status" element={<StatusScreen />} />
          <Route path="/boodschappen/activity" element={<ActivityScreen />} />

          {/* Agenda module */}
          <Route path="/agenda" element={<WeekScreen />} />
          <Route path="/agenda/mensen" element={<PeopleScreen />} />
          <Route path="/agenda/wijzigingen" element={<ChangesScreen />} />

          {/* Todo module */}
          <Route path="/taken" element={<TodosScreen />} />

          {/* Recepten module. Static sub-paths precede /recepten/:id so the param
              route does not swallow them. */}
          <Route path="/recepten" element={<ReceptenScreen />} />
          <Route path="/recepten/import" element={<RecipeImportScreen />} />
          <Route path="/recepten/genereren" element={<RecipeSuggestScreen />} />
          <Route path="/recepten/instellingen" element={<RecipeSettingsScreen />} />
          <Route path="/recepten/:id" element={<RecipeScreen />} />
          <Route path="/recepten/:id/koken" element={<CookModeScreen />} />

          {/* Pakket module — a read-only proxy screen, no sync loop. */}
          <Route path="/pakket" element={<PakketScreen />} />

          {/* Wordle module */}
          <Route path="/woordje" element={<WordleScreen />} />

          {/* Morse module — Jurren's own; the screen redirects anyone else. */}
          <Route path="/morse" element={<MorseScreen />} />
          {/* Schaak module — Jurren's own; the screen redirects anyone else. */}
          <Route path="/schaak" element={<SchaakScreen />} />

          {/* Huub — the Signal assistant's settings; online-only, no sync loop. */}
          <Route path="/huub" element={<HuubScreen />} />
          {/* He was Huib until 2026-11-04; an old bookmark still lands. */}
          <Route path="/huib" element={<Navigate to="/huub" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ErrorBoundary>
    </div>
  );
}
